import { AccountService } from './../account/account.service';
import {
  Component,
  OnInit,
  Output,
  EventEmitter,
  ViewChild,
  AfterViewInit,
  OnDestroy,
  inject,
} from '@angular/core';
import { UntypedFormGroup, FormBuilder } from '@angular/forms';
import { Router } from '@angular/router';
import { StorageService } from '../../services/storage/storage.service';
import { DashboardService, StaffMenuItem } from './dashboard.service';
import { SharedService } from '../../services/shared/shared.service';
import {
  QuickMenuItem,
  QuickMenuService,
} from './../../components/side-nav/quick-menu/quick-menu.service';
import { PredefinedTemplatesService } from './../../services/edit/predefined-templates.service';
import { NgxMasonryOptions, NgxMasonryComponent } from 'ngx-masonry';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';

@Component({
  selector: 'ag-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
})
export class DashboardComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('masonry') masonry: NgxMasonryComponent;
  @Output() viewQuick = new EventEmitter<any>();

  searchText: UntypedFormGroup;
  filterStaffList: StaffMenuItem[] = [];
  staffList: StaffMenuItem[] = [];
  quickMenuList: QuickMenuItem[] = [];
  masonryKnights: NgxMasonryOptions = {
    gutter: 20,
    percentPosition: true,
    initLayout: true,
    resize: true,
    horizontalOrder: true,
    animations: {}, //https://www.npmjs.com/package/ngx-masonry for animations
  };

  expandedAll: boolean = false;
  showCustomerCarePage: boolean = true;

  private _userPreferences: any;

  private _router = inject(Router);
  private _fb = inject(FormBuilder);
  private _quickMenuService = inject(QuickMenuService);
  private _accountService = inject(AccountService);
  private _dashboardService = inject(DashboardService);
  private _sharedService = inject(SharedService);
  private _storageService = inject(StorageService);
  private _predefinedTemplatesService = inject(PredefinedTemplatesService);

  private _destroyed$: Subject<void> = new Subject<void>();

  ngOnInit() {
    this.searchText = this._fb.group({ searchInput: '' });

    this._storageService.setItem('currentUrl', this._router.url);

    this._quickMenuService.quickMenuItems$.pipe(takeUntil(this._destroyed$)).subscribe((data) => {
      if (data) {
        this.quickMenuList = data;
      }
    });

    this._accountService.userPreferences$
      .pipe(takeUntil(this._destroyed$))
      .subscribe((userPrefences) => {
        if (userPrefences) {
          this._userPreferences = userPrefences;
        }
      });

    this._dashboardService.staffDashboardMenu$
      .pipe(takeUntil(this._destroyed$))
      .subscribe((staffList) => {
        if (!staffList) {
          return;
        }

        this.staffList = staffList;

        this._expandDefaultMenu();

        this.filterStaffList = [...staffList];
        this.expandedAll = !this.filterStaffList.some((m) => !m.expanded);

        this._masonryLayoutComplete();
      });
  }

  private _expandDefaultMenu() {
    if (!this.staffList.some((m) => m.hasOwnProperty('expanded'))) {
      const defaultMenuLabel = this._userPreferences
        ?.find((u) => u.name == 'DefaultStaffTab')
        ?.preferenceList?.find((p) => p.isChecked);

      const defaultMenu = this.staffList.find((m) => m.label == defaultMenuLabel?.label);
      if (defaultMenu) {
        defaultMenu.expanded = true;
      }
    }
  }

  ngAfterViewInit(): void {
    this.showCustomerCarePage = this._predefinedTemplatesService.showCustomerCarePage();
    setTimeout(() => {
      this._predefinedTemplatesService.highlightStaffLink();
    }, 400);

    if (this.searchText) {
      this.searchText.controls.searchInput.valueChanges
        .pipe(debounceTime(250))
        .subscribe((value) => {
          this.filterStaffList = [...this.staffList];
          if (value.length > 0) {
            const filterCondition = value.toLowerCase();

            this.filterStaffList = this.filterStaffList.map((m) => {
              //clone
              m = { ...m };

              //filter menu items that match or have subitems
              m.items = m.items.filter(
                (i) => i.subItems.length > 0 || i.label.toLowerCase().includes(filterCondition),
              );

              //filter subItems that match
              m.items = m.items.map((i) => {
                //clone
                i = { ...i };

                //filter those with subItem
                if (i.subItems.length > 0) {
                  i.subItems = i.subItems.filter((i) =>
                    i.label.toLowerCase().includes(filterCondition),
                  );
                  return i.subItems.length > 0 ? i : null;
                }

                return i;
              });

              //filter item with subItems
              m.items = m.items.filter((i) => i != null);

              return m;
            });

            //filter menu with items
            this.filterStaffList = this.filterStaffList.filter((m) => m.items.length > 0);
            this.filterStaffList.forEach((m) => (m.expanded = true));
          }

          this._masonryLayoutComplete();
        });

      //TODO AutoFucos not working with directive
      // $(`#typeahead-staff`).focus();
    }

    this._masonryLayoutComplete();
  }

  ngOnDestroy(): void {
    this._predefinedTemplatesService.addRemoveCurrentClass(-1); // remove tab highlight
    this._destroyed$.next();
    this._destroyed$.complete();
  }

  quickmenuLink(item) {
    this._dashboardService.goToMenuItem(item.menuItem);
  }

  addToQuickMenu(id, item) {
    const body = [
      {
        menuId: id,
        menuItem: item,
      },
    ];

    this._quickMenuService.addQuickMenu(body).subscribe(
      (data) => {},
      (errorMsg: unknown) => {
        if (typeof errorMsg === 'string') {
          this._sharedService.openSnackBar(errorMsg, 'ok');
        } else {
          this._sharedService.openSnackBar('Failed to add quick link', 'ok');
        }
      },
    );
  }

  clearDash() {
    this.searchText?.controls.searchInput.setValue('');
  }

  toggleMenu(menu) {
    menu.expanded = !menu.expanded;

    this.staffList.find((m) => m.menuId == menu.menuId).expanded = menu.expanded;

    //set the toogle all state
    this.expandedAll = !this.filterStaffList.some((m) => !m.expanded);

    this._masonryLayoutComplete();
  }

  toggleAll() {
    this.expandedAll = this.filterStaffList.some((m) => !m.expanded);

    this.filterStaffList.forEach((m) => (m.expanded = this.expandedAll));
    this.staffList.forEach((m) => (m.expanded = this.expandedAll));

    this._masonryLayoutComplete();
  }

  private _masonryLayoutComplete() {
    // https://github.com/wynfred/ngx-masonry
    // added to maintain order of grids
    if (this.masonry) {
      this.masonry.layout();
    }
  }

  itemClick(item) {
    this._dashboardService.goToMenuItem(item);
  }
}
